import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Building2, ChevronDown, ChevronRight, Folder, FolderOpen, Upload, FileText, Image as ImageIcon,
  Trash2, Search, Sun, Flame, ExternalLink, FileSpreadsheet, File as FileIcon, Camera,
  Receipt, Handshake, Package, MessageSquare, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ProjectNotes } from "@/components/ProjectNotes";
import { InvoiceStatus } from "@/components/InvoiceStatus";
import { DuplicateFinder } from "@/components/kunden/DuplicateFinder";

const OHNE_AG = "__ohne__";

// Kategorien für Kunden-Akte
export const CATEGORIES = [
  { key: "angebote",    label: "Angebote",   icon: Handshake,       color: "text-sky-600 dark:text-sky-400" },
  { key: "rechnungen",  label: "Rechnungen", icon: Receipt,         color: "text-emerald-600 dark:text-emerald-400" },
  { key: "fotos",       label: "Fotos",      icon: Camera,          color: "text-amber-600 dark:text-amber-400" },
  { key: "vertraege",   label: "Verträge",   icon: FileSpreadsheet, color: "text-purple-600 dark:text-purple-400" },
  { key: "lieferung",   label: "Lieferung",  icon: Package,         color: "text-cyan-600 dark:text-cyan-400" },
  { key: "sonstiges",   label: "Sonstiges",  icon: FileIcon,        color: "text-muted-foreground" },
];
const CAT_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

function fileMeta(d) {
  const name = d.name || "";
  const ct = (d.content_type || "").toLowerCase();
  const ext = name.toLowerCase().split(".").pop();
  const isImage = ["jpg", "jpeg", "png", "webp", "gif"].includes(ext) || ct.startsWith("image/");
  const isPdf = ext === "pdf" || ct.includes("pdf");
  const isOffice = ["doc", "docx", "xls", "xlsx"].includes(ext);
  return { isImage, isPdf, isOffice };
}

function humanSize(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function docUrl(path) {
  return `${process.env.REACT_APP_BACKEND_URL}/api/documents/${path}`;
}

function DocPreview({ d }) {
  const { isImage, isPdf } = fileMeta(d);
  const url = docUrl(d.path);
  if (isImage) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block relative aspect-[4/3] rounded-md overflow-hidden border border-border bg-secondary/40 hover:border-primary/60">
        <img src={url} alt={d.name} loading="lazy" className="w-full h-full object-cover" />
      </a>
    );
  }
  if (isPdf) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="block relative aspect-[4/3] rounded-md overflow-hidden border border-border bg-secondary/40 hover:border-primary/60"
        title="PDF anzeigen"
      >
        <object data={`${url}#toolbar=0&navpanes=0&view=Fit`} type="application/pdf" className="w-full h-full pointer-events-none">
          <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground p-2 gap-1">
            <FileText className="h-8 w-8" />
            <span className="text-[10px] uppercase tracking-wider">PDF</span>
          </div>
        </object>
      </a>
    );
  }
  // Office/Text fallback: icon tile
  return (
    <a href={url} target="_blank" rel="noreferrer" className="flex flex-col items-center justify-center aspect-[4/3] rounded-md border border-border bg-secondary/40 hover:border-primary/60 text-muted-foreground gap-1">
      {fileMeta(d).isOffice ? <FileSpreadsheet className="h-8 w-8" /> : <FileIcon className="h-8 w-8" />}
      <span className="text-[10px] uppercase tracking-wider">{(d.name.split(".").pop() || "Datei").toUpperCase()}</span>
    </a>
  );
}

export default function Kunden() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [auftraggeber, setAuftraggeber] = useState([]);
  const [expandedAg, setExpandedAg] = useState({});
  const [expandedKunde, setExpandedKunde] = useState({});
  const [expandedCat, setExpandedCat] = useState({}); // `${projectId}::${catKey}` -> bool
  const [docsCache, setDocsCache] = useState({}); // projectId -> [docs]
  const [uploading, setUploading] = useState({}); // `${projectId}::${catKey}` -> { done, total }
  const [dragOver, setDragOver] = useState({}); // `${projectId}::${catKey}` -> bool
  const [unreadByProject, setUnreadByProject] = useState({}); // projectId -> count
  const [unreadInternalByProject, setUnreadInternalByProject] = useState({}); // projectId -> count (internal, staff only)
  const [q, setQ] = useState("");
  const [noteSearch, setNoteSearch] = useState("");
  const [noteResults, setNoteResults] = useState(null); // null = idle, [] = no results

  const isStaff = ["admin", "innendienst"].includes(user?.role);

  const runNoteSearch = async () => {
    const term = noteSearch.trim();
    if (term.length < 2) { setNoteResults(null); return; }
    try {
      const [pub, internal] = await Promise.all([
        api.get(`/projects/notes/search?q=${encodeURIComponent(term)}`).then((r) => r.data).catch(() => []),
        isStaff
          ? api.get(`/projects/notes/search?q=${encodeURIComponent(term)}&internal=true`).then((r) => r.data).catch(() => [])
          : Promise.resolve([]),
      ]);
      const combined = [
        ...pub.map((n) => ({ ...n, _internal: false })),
        ...internal.map((n) => ({ ...n, _internal: true })),
      ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setNoteResults(combined);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Suche fehlgeschlagen");
    }
  };

  const load = async () => {
    try {
      const [p, a, unread, unreadInt] = await Promise.all([
        api.get("/projects").then((x) => x.data),
        api.get("/auftraggeber").then((x) => x.data).catch(() => []),
        api.get("/projects/notes/unread-counts").then((x) => x.data).catch(() => ({})),
        isStaff
          ? api.get("/projects/notes/unread-counts?internal=true").then((x) => x.data).catch(() => ({}))
          : Promise.resolve({}),
      ]);
      setProjects(p || []);
      setAuftraggeber(a || []);
      setUnreadByProject(unread || {});
      setUnreadInternalByProject(unreadInt || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen");
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll unread counts every 30s
  useEffect(() => {
    const t = setInterval(() => {
      api.get("/projects/notes/unread-counts").then((x) => setUnreadByProject(x.data || {})).catch(() => {});
      if (isStaff) api.get("/projects/notes/unread-counts?internal=true").then((x) => setUnreadInternalByProject(x.data || {})).catch(() => {});
    }, 30_000);
    return () => clearInterval(t);
  }, [isStaff]);

  const clearUnreadForProject = (pid, internal = false) => {
    if (internal) {
      setUnreadInternalByProject((s) => { if (!s[pid]) return s; const n = { ...s }; delete n[pid]; return n; });
    } else {
      setUnreadByProject((s) => { if (!s[pid]) return s; const n = { ...s }; delete n[pid]; return n; });
    }
  };

  // Group by Auftraggeber, then by kunde_name (alphabetically)
  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    const byAg = new Map();
    for (const a of auftraggeber) byAg.set(a.id, { id: a.id, name: a.name, kunden: new Map() });
    byAg.set(OHNE_AG, byAg.get(OHNE_AG) || { id: OHNE_AG, name: "Ohne Auftraggeber", kunden: new Map() });

    for (const p of projects) {
      const agId = p.auftraggeber_id || OHNE_AG;
      if (!byAg.has(agId)) byAg.set(agId, { id: agId, name: "—", kunden: new Map() });
      const bucket = byAg.get(agId);
      const key = (p.kunde_name || "—").trim();
      const kundeMatchesQ = !term
        || key.toLowerCase().includes(term)
        || (p.kunde_adresse || "").toLowerCase().includes(term)
        || (p.kunde_ort || "").toLowerCase().includes(term);
      const agMatchesQ = !term || (bucket.name || "").toLowerCase().includes(term);
      if (!kundeMatchesQ && !agMatchesQ) continue;
      if (!bucket.kunden.has(key)) bucket.kunden.set(key, []);
      bucket.kunden.get(key).push(p);
    }
    const list = Array.from(byAg.values()).map((ag) => ({
      ...ag,
      kunden: Array.from(ag.kunden.entries())
        .sort(([a], [b]) => a.localeCompare(b, "de", { sensitivity: "base" }))
        .map(([name, projs]) => ({ name, projs })),
    }));
    list.sort((a, b) => {
      if (a.id === OHNE_AG) return 1;
      if (b.id === OHNE_AG) return -1;
      return (a.name || "").localeCompare(b.name || "", "de", { sensitivity: "base" });
    });
    return term ? list.filter((g) => g.kunden.length > 0) : list;
  }, [projects, auftraggeber, q]);

  const totalKunden = useMemo(() => groups.reduce((s, g) => s + g.kunden.length, 0), [groups]);

  const toggleAg = (id) => setExpandedAg((s) => ({ ...s, [id]: !s[id] }));

  const toggleKunde = async (kundeKey, projs) => {
    const key = kundeKey;
    const nowOpen = !expandedKunde[key];
    setExpandedKunde((s) => ({ ...s, [key]: nowOpen }));
    if (nowOpen) {
      const missing = projs.filter((p) => !docsCache[p.id]);
      if (missing.length) {
        const results = await Promise.all(
          missing.map((p) =>
            api.get(`/projects/${p.id}/documents`).then((x) => [p.id, x.data]).catch(() => [p.id, []])
          )
        );
        setDocsCache((s) => ({ ...s, ...Object.fromEntries(results) }));
      }
    }
  };

  const toggleCat = (projectId, catKey) => {
    const k = `${projectId}::${catKey}`;
    setExpandedCat((s) => ({ ...s, [k]: !s[k] }));
  };

  const upload = async (projectId, catKey, files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    const bk = `${projectId}::${catKey}`;
    setUploading((s) => ({ ...s, [bk]: { done: 0, total: list.length } }));
    setExpandedCat((s) => ({ ...s, [bk]: true }));
    let ok = 0, fail = 0;
    for (const file of list) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post(`/projects/${projectId}/documents?category=${encodeURIComponent(catKey)}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setDocsCache((s) => ({ ...s, [projectId]: [...(s[projectId] || []), data] }));
        ok++;
      } catch (e) {
        fail++;
        toast.error(`${file.name}: ${formatApiErrorDetail(e.response?.data?.detail) || "Upload fehlgeschlagen"}`);
      }
      setUploading((s) => ({ ...s, [bk]: { done: (s[bk]?.done || 0) + 1, total: list.length } }));
    }
    setUploading((s) => { const n = { ...s }; delete n[bk]; return n; });
    if (ok) toast.success(`${ok} Datei${ok === 1 ? "" : "en"} in "${CAT_MAP[catKey]?.label || catKey}" hochgeladen${fail ? ` (${fail} Fehler)` : ""}`);
  };

  const onCatDragOver = (projectId, catKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    const bk = `${projectId}::${catKey}`;
    if (!dragOver[bk]) setDragOver((s) => ({ ...s, [bk]: true }));
  };
  const onCatDragLeave = (projectId, catKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    const bk = `${projectId}::${catKey}`;
    setDragOver((s) => ({ ...s, [bk]: false }));
  };
  const onCatDrop = (projectId, catKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    const bk = `${projectId}::${catKey}`;
    setDragOver((s) => ({ ...s, [bk]: false }));
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) upload(projectId, catKey, files);
  };

  const removeDoc = async (projectId, docId) => {
    if (!confirm("Datei wirklich löschen?")) return;
    try {
      await api.delete(`/projects/${projectId}/documents/${docId}`);
      setDocsCache((s) => ({ ...s, [projectId]: (s[projectId] || []).filter((d) => d.id !== docId) }));
      toast.success("Gelöscht");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Löschen fehlgeschlagen");
    }
  };

  const moveDoc = async (projectId, docId, category) => {
    try {
      await api.patch(`/projects/${projectId}/documents/${docId}`, { category });
      setDocsCache((s) => ({
        ...s,
        [projectId]: (s[projectId] || []).map((d) => (d.id === docId ? { ...d, category } : d)),
      }));
      toast.success(`Verschoben nach "${CAT_MAP[category]?.label || category}"`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verschieben fehlgeschlagen");
    }
  };

  return (
    <div className="p-4 lg:p-8 space-y-6 animate-fade-up" data-testid="kunden-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline">Kunden-Akte</div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mt-2">Kunden</h1>
          <p className="text-muted-foreground mt-1 text-sm lg:text-base">
            Alle Kunden alphabetisch, gruppiert nach Auftraggeber. Datei-Ablage in Kategorien.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" data-testid="kunden-count">{totalKunden} Kunden</Badge>
          {isStaff && <DuplicateFinder onMerged={load} />}
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Kunde, Auftraggeber, Ort…"
              className="pl-8 w-64"
              data-testid="kunden-search"
            />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); runNoteSearch(); }} className="relative flex items-center gap-1">
            <div className="relative">
              <MessageSquare className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-primary" />
              <Input
                value={noteSearch}
                onChange={(e) => setNoteSearch(e.target.value)}
                placeholder="In Nachrichten suchen…"
                className="pl-8 w-64"
                data-testid="notes-search-input"
              />
            </div>
            <Button type="submit" size="sm" variant="outline" data-testid="notes-search-btn">Suchen</Button>
            {noteResults !== null && (
              <Button type="button" size="sm" variant="ghost" onClick={() => { setNoteResults(null); setNoteSearch(""); }} data-testid="notes-search-clear">
                <XCircle className="h-4 w-4" />
              </Button>
            )}
          </form>
        </div>
      </div>

      {noteResults !== null && (
        <Card className="border-primary/50" data-testid="notes-search-results">
          <CardContent className="p-4">
            <div className="text-sm font-medium mb-3 flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-primary" />
              Nachrichten-Treffer: <span className="text-primary">{noteResults.length}</span>
            </div>
            {noteResults.length === 0 ? (
              <div className="text-xs text-muted-foreground text-center py-4">Keine Treffer für „{noteSearch}"</div>
            ) : (
              <ul className="divide-y divide-border max-h-80 overflow-y-auto">
                {noteResults.slice(0, 100).map((n) => (
                  <li key={n.id} className="py-2" data-testid={`note-hit-${n.id}`}>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span className={`font-semibold ${n._internal ? "text-amber-600 dark:text-amber-400" : "text-primary"}`}>
                        {n._internal ? "🔒 INTERN" : "Kunde"}
                      </span>
                      <span>·</span>
                      <span className="font-medium text-foreground truncate">{n.kunde_name}</span>
                      <span>·</span>
                      <span>{n.user_name}</span>
                      <span>·</span>
                      <span>{new Date(n.created_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                    <div className="text-sm mt-1 line-clamp-2">{n.body}</div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {groups.length === 0 && (
          <Card className="border-border"><CardContent className="p-8 text-center text-sm text-muted-foreground">Keine Einträge.</CardContent></Card>
        )}
        {groups.map((ag) => (
          <Card key={ag.id} className="border-border" data-testid={`kunden-ag-${ag.id}`}>
            <button
              type="button"
              onClick={() => toggleAg(ag.id)}
              className="w-full flex items-center justify-between p-4 lg:p-5 hover:bg-secondary/40 transition-colors text-left"
              data-testid={`kunden-ag-toggle-${ag.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <Building2 className="h-5 w-5 text-primary shrink-0" />
                <div className="min-w-0">
                  <div className="font-heading text-lg font-semibold truncate">{ag.name}</div>
                  <div className="text-xs text-muted-foreground">{ag.kunden.length} Kunden</div>
                </div>
              </div>
              {expandedAg[ag.id] ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
            </button>

            {expandedAg[ag.id] && (
              <div className="border-t border-border divide-y divide-border">
                {ag.kunden.length === 0 && (
                  <div className="p-4 text-sm text-muted-foreground">Keine Kunden.</div>
                )}
                {ag.kunden.map(({ name, projs }) => {
                  const uid = `${ag.id}::${name}`;
                  const isOpen = !!expandedKunde[uid];
                  return (
                    <div key={uid} data-testid={`kunde-row-${name}`}>
                      <button
                        type="button"
                        onClick={() => toggleKunde(uid, projs)}
                        className="w-full flex items-center justify-between px-4 lg:px-6 py-3 hover:bg-secondary/40 text-left"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {isOpen ? <FolderOpen className="h-4 w-4 text-primary shrink-0" /> : <Folder className="h-4 w-4 text-muted-foreground shrink-0" />}
                          <div className="min-w-0">
                            <div className="font-medium truncate">{name}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {projs[0]?.kunde_adresse}{projs[0]?.kunde_ort ? `, ${projs[0].kunde_ort}` : ""}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {(() => {
                            const kUnread = projs.reduce((s, p) => s + (unreadByProject[p.id] || 0) + (unreadInternalByProject[p.id] || 0), 0);
                            return kUnread > 0 ? (
                              <span className="rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-2 h-5 inline-flex items-center justify-center" data-testid={`kunde-unread-${name}`} title={`${kUnread} neue Nachricht(en)`}>
                                {kUnread}
                              </span>
                            ) : null;
                          })()}
                          {projs.some((p) => p.project_type === "pv") && (
                            <Badge variant="outline" className="gap-1"><Sun className="h-3 w-3" />PV</Badge>
                          )}
                          {projs.some((p) => p.project_type === "waermepumpe") && (
                            <Badge variant="outline" className="gap-1"><Flame className="h-3 w-3" />WP</Badge>
                          )}
                          {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        </div>
                      </button>

                      {isOpen && (
                        <div className="bg-secondary/20 px-4 lg:px-8 py-4 space-y-4">
                          {projs.map((p) => {
                            const allDocs = docsCache[p.id] || [];
                            // Group docs by category
                            const byCat = {};
                            for (const d of allDocs) {
                              const c = d.category || "sonstiges";
                              (byCat[c] = byCat[c] || []).push(d);
                            }
                            return (
                              <div key={p.id} className="rounded-md border border-border bg-card p-3" data-testid={`kunde-projekt-${p.id}`}>
                                <div className="flex items-center justify-between gap-2 mb-3">
                                  <div className="flex items-center gap-2 min-w-0">
                                    {p.project_type === "pv" ? <Sun className="h-4 w-4 text-primary shrink-0" /> : <Flame className="h-4 w-4 text-orange-500 shrink-0" />}
                                    <div className="text-sm font-medium truncate">
                                      {p.project_type === "pv" ? "Photovoltaik" : "Wärmepumpe"}
                                    </div>
                                  </div>
                                  <Link to={`/projekte?deeplink=${p.id}`} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid={`kunde-open-projekt-${p.id}`}>
                                    Projekt öffnen <ExternalLink className="h-3 w-3" />
                                  </Link>
                                </div>

                                <div className="space-y-2">
                                  {CATEGORIES.map((cat) => {
                                    const docs = byCat[cat.key] || [];
                                    const catUid = `${p.id}::${cat.key}`;
                                    const catOpen = expandedCat[catUid] ?? docs.length > 0;
                                    const uploadState = uploading[catUid];
                                    const isDragging = !!dragOver[catUid];
                                    const Icon = cat.icon;
                                    return (
                                      <div
                                        key={cat.key}
                                        className={`rounded border transition-colors ${isDragging ? "border-primary bg-primary/5" : "border-border/70"}`}
                                        onDragOver={(e) => onCatDragOver(p.id, cat.key, e)}
                                        onDragLeave={(e) => onCatDragLeave(p.id, cat.key, e)}
                                        onDrop={(e) => onCatDrop(p.id, cat.key, e)}
                                        data-testid={`cat-${p.id}-${cat.key}`}
                                      >
                                        <button
                                          type="button"
                                          onClick={() => toggleCat(p.id, cat.key)}
                                          className="w-full flex items-center justify-between px-3 py-2 hover:bg-secondary/40 text-left"
                                        >
                                          <div className="flex items-center gap-2 min-w-0">
                                            <Icon className={`h-4 w-4 shrink-0 ${cat.color}`} />
                                            <span className="text-sm font-medium">{cat.label}</span>
                                            <span className="text-[11px] text-muted-foreground">({docs.length})</span>
                                            {isDragging && <span className="text-[10px] text-primary font-medium uppercase tracking-wider ml-1">Loslassen zum Hochladen</span>}
                                          </div>
                                          <div className="flex items-center gap-1 shrink-0">
                                            <label className="inline-flex items-center gap-1 cursor-pointer text-[11px] text-primary hover:underline px-2" onClick={(e) => e.stopPropagation()}>
                                              <Upload className="h-3 w-3" />
                                              {uploadState
                                                ? `${uploadState.done}/${uploadState.total}`
                                                : "Hochladen"}
                                              <input
                                                type="file"
                                                multiple
                                                className="hidden"
                                                onChange={(e) => { const fs = e.target.files; e.target.value = ""; if (fs?.length) upload(p.id, cat.key, fs); }}
                                                data-testid={`kunde-upload-${p.id}-${cat.key}`}
                                              />
                                            </label>
                                            {catOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                          </div>
                                        </button>

                                        {catOpen && (
                                          <div className="border-t border-border/70 p-3">
                                            {docs.length === 0 ? (
                                              <div className="text-[11px] text-muted-foreground text-center py-4">
                                                Noch keine Dateien — <span className="text-primary">Dateien hierher ziehen</span> oder oben „Hochladen".
                                              </div>
                                            ) : (
                                              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                                                {docs.map((d) => (
                                                  <div key={d.id} className="space-y-1" data-testid={`kunde-doc-${d.id}`}>
                                                    <DocPreview d={d} />
                                                    <div className="flex items-start justify-between gap-1">
                                                      <a
                                                        href={docUrl(d.path)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-xs truncate hover:text-primary flex-1 min-w-0"
                                                        title={d.name}
                                                      >
                                                        {d.name}
                                                      </a>
                                                      {isStaff && (
                                                        <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive shrink-0" onClick={() => removeDoc(p.id, d.id)} data-testid={`kunde-doc-delete-${d.id}`}>
                                                          <Trash2 className="h-3 w-3" />
                                                        </Button>
                                                      )}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                      <span className="text-[10px] text-muted-foreground">{humanSize(d.size)}</span>
                                                      {isStaff && (
                                                        <Select value={d.category || "sonstiges"} onValueChange={(v) => moveDoc(p.id, d.id, v)}>
                                                          <SelectTrigger className="h-6 text-[10px] flex-1 min-w-0" data-testid={`kunde-doc-cat-${d.id}`}>
                                                            <SelectValue />
                                                          </SelectTrigger>
                                                          <SelectContent>
                                                            {CATEGORIES.map((c) => (
                                                              <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                                                            ))}
                                                          </SelectContent>
                                                        </Select>
                                                      )}
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* Rechnungsstatus */}
                                <InvoiceStatus
                                  projectId={p.id}
                                  initial={p.invoice}
                                  isStaff={isStaff}
                                  onChange={(inv) => {
                                    setProjects((arr) => arr.map((x) => (x.id === p.id ? { ...x, invoice: inv } : x)));
                                  }}
                                />

                                {/* Kunden-Nachrichten */}
                                <ProjectNotes
                                  projectId={p.id}
                                  unread={unreadByProject[p.id] || 0}
                                  onRead={clearUnreadForProject}
                                />
                                {/* Interne Notizen (nur Admin/Innendienst) */}
                                {isStaff && (
                                  <ProjectNotes
                                    projectId={p.id}
                                    internal={true}
                                    unread={unreadInternalByProject[p.id] || 0}
                                    onRead={clearUnreadForProject}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
